import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUpload, FiChevronDown, FiChevronUp, FiCheckCircle, FiArrowRight,
} from 'react-icons/fi';
import { diagnosisApi, resumeApi } from '../../services/api';

// ── helpers ────────────────────────────────────────────────────────────────────

function extractTextFromContent(content) {
  if (!content) return '';
  const parts = [];
  if (content.name) parts.push(`Name: ${content.name}`);
  if (content.summary) parts.push(`Summary: ${content.summary}`);
  if (Array.isArray(content.experience)) {
    parts.push('Experience:');
    content.experience.forEach(exp => {
      if (!exp) return;
      parts.push(`  ${exp.title || ''} at ${exp.company || ''}`);
      const details = exp.details || exp.bullets || [];
      (Array.isArray(details) ? details : []).forEach(d => {
        const text = typeof d === 'string' ? d : (d?.details || '');
        if (text) parts.push(`    - ${text}`);
      });
    });
  }
  if (Array.isArray(content.education)) {
    parts.push('Education:');
    content.education.forEach(edu => {
      if (!edu) return;
      parts.push(`  ${edu.degree || ''} at ${edu.school || edu.institution || ''}`);
    });
  }
  if (content.skills) {
    const skills = Array.isArray(content.skills) ? content.skills : [];
    const names = skills.map(s => (typeof s === 'string' ? s : s?.name || '')).filter(Boolean);
    if (names.length) parts.push(`Skills: ${names.join(', ')}`);
  }
  return parts.join('\n');
}

// ── Style tokens ─────────────────────────────────────────────────────────────

const TEAL = '#5AAFA7';
const card = {
  background: '#fff', borderRadius: 14, padding: '28px 32px',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
};

// ── Progress bar ───────────────────────────────────────────────────────────────

function ProgressBar({ step }) {
  const steps = ['Upload Resume', 'Quick Profile', 'Your Diagnosis'];
  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {steps.map((label, i) => (
          <React.Fragment key={i}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: i < step ? TEAL : i === step ? TEAL : '#e2e8f0',
                color: i <= step ? '#fff' : '#94a3b8',
                fontWeight: 700, fontSize: 13, flexShrink: 0,
                boxShadow: i === step ? `0 0 0 4px ${TEAL}22` : 'none',
                transition: 'all 0.3s',
              }}>
                {i < step ? <FiCheckCircle size={16} /> : i + 1}
              </div>
              <span style={{
                fontSize: 11, marginTop: 6, textAlign: 'center', lineHeight: 1.3,
                color: i <= step ? TEAL : '#94a3b8',
                fontWeight: i === step ? 700 : 400,
              }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{
                flex: 2, height: 2, marginTop: 16,
                background: i < step ? TEAL : '#e2e8f0',
                transition: 'background 0.3s',
              }} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ── Step 1: Upload Resume ──────────────────────────────────────────────────────

function Step1({ onNext }) {
  const [text, setText] = useState('');
  const [tab, setTab] = useState('paste');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  const isValid = wordCount >= 80;

  const handleFile = async (file) => {
    if (!file) return;
    if ((file.name || '').endsWith('.txt')) {
      const reader = new FileReader();
      reader.onload = (ev) => { setText(ev.target.result); setTab('paste'); };
      reader.readAsText(file);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await resumeApi.uploadResume(formData);
      const resume = res.data?.resume || res.data;
      const versions = resume?.versions || [];
      const content = versions[0]?.content || resume?.content;
      if (content) {
        setText(extractTextFromContent(content));
        setTab('paste');
      }
    } catch (err) {
      console.error('File parse error:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: '#1e293b', margin: 0 }}>
          Get your Resume Diagnosis in 5 minutes
        </h2>
        <p style={{ fontSize: 14, color: '#64748b', margin: '8px 0 0' }}>
          We&apos;ll tell you exactly what&apos;s holding your job search back —&nbsp;
          <strong>not generic tips, your specific issues.</strong>
        </p>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e2e8f0', marginTop: 24, marginBottom: 0 }}>
        {[['paste', '✏️  Paste Text'], ['upload', '📁  Upload File']].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)} aria-label={`Tab: ${label}`} aria-selected={tab === key} style={{
            padding: '8px 22px', fontSize: 13, fontWeight: tab === key ? 700 : 400,
            color: tab === key ? TEAL : '#64748b',
            background: 'none', border: 'none', cursor: 'pointer',
            borderBottom: tab === key ? `2px solid ${TEAL}` : '2px solid transparent',
            marginBottom: -2,
          }}>{label}</button>
        ))}
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === 'paste' ? (
          <>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder={"Paste your full resume here…\n\nInclude experience, education, skills, and any other sections."}
              style={{
                width: '100%', minHeight: 260, padding: '14px 16px', fontSize: 13,
                border: `2px solid ${isValid ? TEAL : '#e2e8f0'}`, borderRadius: 10,
                outline: 'none', fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical',
                boxSizing: 'border-box', transition: 'border-color 0.2s',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>🔒 Private. Never stored. Used only for your diagnosis.</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: isValid ? TEAL : '#ef4444' }}>
                {wordCount} / 80 words min
              </span>
            </div>
          </>
        ) : (
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            style={{
              border: `2px dashed ${dragOver ? TEAL : '#cbd5e1'}`, borderRadius: 12,
              padding: '48px 24px', textAlign: 'center',
              background: dragOver ? '#f0fdf4' : uploading ? '#f0fdf4' : '#fafbff',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
            onClick={() => fileRef.current?.click()}
          >
            <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt"
              onChange={e => handleFile(e.target.files[0])} style={{ display: 'none' }} />
            {uploading ? (
              <p style={{ color: TEAL, fontWeight: 600, margin: 0 }}>Parsing your resume…</p>
            ) : (
              <>
                <FiUpload size={30} color="#94a3b8" />
                <p style={{ fontSize: 14, color: '#475569', margin: '10px 0 4px' }}>
                  Drop your file here, or <span style={{ color: TEAL, fontWeight: 600 }}>browse</span>
                </p>
                <p style={{ fontSize: 12, color: '#94a3b8', margin: 0 }}>PDF, DOCX, TXT supported</p>
              </>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        disabled={!isValid}
        onClick={() => onNext(text)}
        aria-label="Analyze my resume"
        style={{
          marginTop: 20, width: '100%', padding: '14px', borderRadius: 10,
          background: isValid ? TEAL : '#e2e8f0', color: isValid ? '#fff' : '#94a3b8',
          fontSize: 15, fontWeight: 700, border: 'none',
          cursor: isValid ? 'pointer' : 'not-allowed', transition: 'all 0.2s',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        Analyze My Resume <FiArrowRight size={16} />
      </button>
    </div>
  );
}

// ── Step 2: Quick Profile ──────────────────────────────────────────────────────

const SENIORITY = ['New grad', '1–3 yrs', '3–5 yrs', '5+ yrs'];
const CHANNELS = ['Online apply', 'Referral', 'Agency / headhunter', 'Cold outreach'];
const VISAS = ['OPT', 'H-1B', 'Citizen / PR', 'Other', 'N/A'];

function Chip({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} aria-pressed={active} style={{
      padding: '6px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
      border: `2px solid ${active ? TEAL : '#e2e8f0'}`,
      background: active ? '#e6f7f6' : '#fff',
      color: active ? TEAL : '#475569', fontWeight: active ? 700 : 400,
      transition: 'all 0.15s',
    }}>{label}</button>
  );
}

function FieldLabel({ children }) {
  return (
    <label style={{
      display: 'block', fontSize: 11, fontWeight: 700, color: '#475569',
      marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em',
    }}>{children}</label>
  );
}

function Step2({ onNext }) {
  const [roles, setRoles] = useState('');
  const [seniority, setSeniority] = useState('');
  const [location, setLocation] = useState('');
  const [showOptional, setShowOptional] = useState(false);
  const [visa, setVisa] = useState('');
  const [channel, setChannel] = useState('');

  const isValid = roles.trim() && seniority && location.trim();

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: '2px solid #e2e8f0', fontSize: 13, outline: 'none',
    boxSizing: 'border-box', fontFamily: 'inherit',
    transition: 'border-color 0.2s',
  };

  return (
    <div>
      <h2 style={{ fontSize: 26, fontWeight: 800, color: '#1e293b', margin: 0 }}>
        One more minute to sharpen your diagnosis
      </h2>
      <p style={{ fontSize: 14, color: '#64748b', margin: '8px 0 24px' }}>
        Just 3 questions. We default everything else.
      </p>

      <div style={{ marginBottom: 18 }}>
        <FieldLabel>Target Role(s) *</FieldLabel>
        <input value={roles} onChange={e => setRoles(e.target.value)}
          placeholder="e.g. Data Engineer, Product Manager, SWE"
          style={inputStyle}
          onFocus={e => e.target.style.borderColor = TEAL}
          onBlur={e => e.target.style.borderColor = '#e2e8f0'}
        />
      </div>

      <div style={{ marginBottom: 18 }}>
        <FieldLabel>Seniority *</FieldLabel>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {SENIORITY.map(opt => (
            <Chip key={opt} label={opt} active={seniority === opt} onClick={() => setSeniority(opt)} />
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <FieldLabel>Target Location *</FieldLabel>
        <input value={location} onChange={e => setLocation(e.target.value)}
          placeholder="e.g. US, NYC, Remote, Bay Area"
          style={inputStyle}
          onFocus={e => e.target.style.borderColor = TEAL}
          onBlur={e => e.target.style.borderColor = '#e2e8f0'}
        />
      </div>

      {/* Optional section */}
      <button type="button" onClick={() => setShowOptional(s => !s)} aria-label={showOptional ? 'Hide optional fields' : 'Add more detail for a sharper diagnosis'} style={{
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: TEAL,
        background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600,
        marginBottom: 12, padding: 0,
      }}>
        {showOptional ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
        Add more detail for a sharper diagnosis
      </button>

      {showOptional && (
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ marginBottom: 14 }}>
            <FieldLabel>Visa Status</FieldLabel>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {VISAS.map(opt => (
                <Chip key={opt} label={opt} active={visa === opt} onClick={() => setVisa(opt)} />
              ))}
            </div>
          </div>
          <div>
            <FieldLabel>Primary Channel</FieldLabel>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {CHANNELS.map(opt => (
                <Chip key={opt} label={opt} active={channel === opt} onClick={() => setChannel(opt)} />
              ))}
            </div>
          </div>
          <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 12, marginBottom: 0 }}>
            If skipped, we assume: US market, no visa constraint, online apply as primary channel.
          </p>
        </div>
      )}

      <button
        type="button"
        disabled={!isValid}
        onClick={() => onNext({ roles, seniority, location, visa, channel })}
        aria-label="Generate diagnosis report"
        style={{
          width: '100%', padding: '14px', borderRadius: 10, fontSize: 15, fontWeight: 700,
          background: isValid ? TEAL : '#e2e8f0', color: isValid ? '#fff' : '#94a3b8',
          border: 'none', cursor: isValid ? 'pointer' : 'not-allowed', transition: 'all 0.2s',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        Generate My Diagnosis <FiArrowRight size={16} />
      </button>
      <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 10 }}>
        This takes about 45 seconds. Really.
      </p>
    </div>
  );
}

// ── Step 3: Loading ────────────────────────────────────────────────────────────

const LOADING_MSGS = [
  'Scanning your work experience…',
  'Checking keyword alignment with target roles…',
  'Evaluating your channel strategy…',
  'Comparing against market benchmarks…',
  'Identifying your top 3 bottlenecks…',
  'Writing your diagnosis…',
];

function Step3Loading({ msgIdx }) {
  const checks = ['Resume parsed', 'Market fit analyzed', 'Channel strategy assessed'];
  return (
    <div style={{ textAlign: 'center', padding: '48px 0' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🩺</div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>
        Reading your resume…
      </h2>
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 28 }}>
        We&apos;re doing what a recruiter does in 6 seconds — but we actually explain it.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '0 auto', maxWidth: 300 }}>
        {checks.map((msg, i) => {
          const done = msgIdx > i * 2;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left' }}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                background: done ? TEAL : '#e2e8f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, color: done ? '#fff' : '#94a3b8', transition: 'all 0.5s',
              }}>
                {done ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 13, color: done ? '#1e293b' : '#94a3b8', fontWeight: done ? 600 : 400 }}>
                {msg}
              </span>
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic', marginTop: 32 }}>
        {LOADING_MSGS[msgIdx % LOADING_MSGS.length]}
      </p>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

export default function ResumeDiagnosis() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [resumeText, setResumeText] = useState('');
  const [msgIdx, setMsgIdx] = useState(0);
  const intervalRef = useRef(null);

  // Cleanup interval on unmount
  useEffect(() => () => clearInterval(intervalRef.current), []);

  const handleStep1 = (text) => {
    setResumeText(text);
    setStep(1);
  };

  const handleStep2 = async (profile) => {
    setStep(2);
    intervalRef.current = setInterval(() => setMsgIdx(i => i + 1), 1500);

    try {
      const channelMap = { 'Online apply': 'online', 'Referral': 'referral', 'Agency / headhunter': 'agency', 'Cold outreach': 'cold' };
      const res = await diagnosisApi.runQuickDiagnosis({
        resume_text: resumeText,
        target_roles: (profile.roles || '').split(',').map(r => r.trim()).filter(Boolean),
        seniority: profile.seniority,
        location: profile.location,
        channel_preference: profile.channel ? (channelMap[profile.channel] || 'online') : 'online',
        visa: profile.visa || null,
      });
      clearInterval(intervalRef.current);
      navigate('/resume-diagnosis/report', { state: { diagnosis: res.data } });
    } catch (err) {
      clearInterval(intervalRef.current);
      console.error('Diagnosis error:', err);
      navigate('/resume-diagnosis/report', { state: { error: true } });
    }
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '32px 16px' }}>
      <ProgressBar step={step} />
      <div style={card}>
        {step === 0 && <Step1 onNext={handleStep1} />}
        {step === 1 && <Step2 onNext={handleStep2} />}
        {step === 2 && <Step3Loading msgIdx={msgIdx} />}
      </div>
    </div>
  );
}
